import React, { useEffect, useMemo, useState } from 'react';
import { ref, get } from 'firebase/database';
import { database } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import { OutreachCard, OutreachTeamTable } from '../components/outreach/MyOutreach';
import { getTeamTransactions } from '../services/transactionService';
import { watchPath } from '../services/crmService';
import { SI_PATH } from '../services/sellerInterestService';
import { isTeamLeaderEmail, COMMISSION } from '../config/commissionPlans';
import { isClosedDeal, isOpenDeal, priceOf, closeDateOf, computeCommission, leaderCommission, capYearStart, leadSourceOf, customPlanOf, capYearStartFor } from '../services/commissionMath';
import { Link } from 'react-router-dom';

// My Performance.
//  Agents (and Admin using View As): own pipeline, volume, and two commission numbers (already net of splits).
//  Admin on their own login: "My Performance" (own deals, full split breakdown, cap progress) beside
//  "Team Performance" (all agents' volume and pipelines). Stacks on phones.
const GOLD = '#D4AF37';
const PIPELINE = ['New', 'Contacted', 'Appointment set'];
const dollars = (n) => `$${Math.round(n || 0).toLocaleString('en-US')}`;
const gridOf = (min) => ({ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`, gap: 12, marginBottom: 12 });

function Tile({ label, value, sub }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e5e5', borderTop: `3px solid ${GOLD}`, borderRadius: 10, padding: 16 }}>
      <div style={{ fontSize: 13, color: '#666', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, margin: '4px 0' }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: '#888' }}>{sub}</div>}
    </div>
  );
}

function SplitTable({ title, s, extra }) {
  const row = (l, v, bold) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontWeight: bold ? 800 : 400 }}><span>{l}</span><span>{v}</span></div>
  );
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 10, padding: 16 }}>
      <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>{title}</h3>
      {row('Gross commission', dollars(s.gross))}
      {s.leader > 0 && row('Team leader share', `- ${dollars(s.leader)}`)}
      {row('eXp (broker) split', `- ${dollars(s.broker)}`)}
      {row('Take-home', dollars(s.net), true)}
      {extra}
      <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>{s.deals} deal{s.deals === 1 ? '' : 's'}</div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <section style={{ background: '#fafafa', border: '1px solid #e5e5e5', borderRadius: 12, padding: 16 }}>
    <h3 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 800 }}>{title}</h3>
    {children}
  </section>
);

export default function MyPerformance() {
  const { user, userRole, realRole, viewAsUser } = useAuth();
  const adminLayout = realRole === 'Admin' && !viewAsUser;
  const [deals, setDeals] = useState([]);
  const [interests, setInterests] = useState([]);
  const [people, setPeople] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.uid) return;
    // Admin loads every team deal (same path as support roles) for Team Performance.
    getTeamTransactions(user.uid, adminLayout ? 'TC' : userRole).then((d) => setDeals(d || [])).catch(() => setDeals([])).finally(() => setLoading(false));
  }, [user?.uid, userRole, adminLayout]);
  // Names and emails for the per-agent breakdown (Admin only).
  useEffect(() => {
    if (!adminLayout) return;
    get(ref(database, 'users')).then((snap) => setPeople(Object.fromEntries(Object.entries(snap.val() || {}).map(([uid, u]) => [uid, { name: u.displayName || u.email || uid, email: u.email || '', settings: u.commissionSettings || {} }])))).catch(() => setPeople({}));
  }, [adminLayout]);
  // Own commission settings (lead-type splits) for non-admin views; admins get everyone's via people.
  const [mySettings, setMySettings] = useState({});
  useEffect(() => {
    if (!user?.uid) return;
    get(ref(database, `users/${user.uid}/commissionSettings`)).then((s) => setMySettings(s.val() || {})).catch(() => setMySettings({}));
  }, [user?.uid]);
  const settingsOf = (uid) => people[uid]?.settings || (uid === user?.uid ? mySettings : {});
  // CRM data is Admin-only in the database rules; other roles show 0 until CRM opens up.
  useEffect(() => (realRole === 'Admin' ? watchPath(SI_PATH, setInterests) : undefined), [realRole]);

  const now = new Date();
  const yearStart = capYearStart(now);
  const ownerOf = (t) => t.ownerUid || user?.uid;
  const mine = useMemo(() => deals.filter((t) => ownerOf(t) === user?.uid), [deals, user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps
  // A custom-plan user (Limited Agent) has her own cap year (capAnniversaryMonth/Day).
  const closedIn = (list, s) => { const start = customPlanOf(s) ? capYearStartFor(new Date(), undefined, s) : yearStart; return list.filter((t) => isClosedDeal(t) && closeDateOf(t) >= start); };
  const myClosed = closedIn(mine);
  const myOpen = mine.filter(isOpenDeal);
  const iAmLeader = isTeamLeaderEmail(user?.email);
  const sellerCount = (uidFilter) => interests.filter((i) => PIPELINE.includes(i.status) && (!uidFilter || i.assignedAgentId === uidFilter || (!i.assignedAgentId && i.alertTo === uidFilter))).length;
  const vol = (list) => dollars(list.reduce((s, t) => s + priceOf(t), 0));
  // Per-agent breakdown (Admin): each owner's deals on their own plan and cap.
  const agentRows = useMemo(() => {
    if (!adminLayout) return [];
    const byOwner = {};
    deals.forEach((t) => { const o = t.ownerUid || user?.uid; (byOwner[o] = byOwner[o] || []).push(t); });
    return Object.entries(byOwner).map(([uid, list]) => {
      const leader = uid === user?.uid ? iAmLeader : isTeamLeaderEmail(people[uid]?.email);
      const c = computeCommission(closedIn(list, settingsOf(uid)), list.filter(isOpenDeal), leader, undefined, settingsOf(uid));
      return { uid, name: people[uid]?.name || (uid === user?.uid ? 'Me' : uid), leader, ...c };
    }).sort((a, b) => b.ytd.gross - a.ytd.gross);
  }, [adminLayout, deals, people, mySettings, user?.uid]); // eslint-disable-line react-hooks/exhaustive-deps
  // Team leader: own deals + override income share one eXp cap stream. Everyone else: their own plan.
  const agentDeals = iAmLeader ? deals.filter((t) => (t.ownerUid || user?.uid) !== user?.uid) : [];
  const comm = iAmLeader
    ? leaderCommission(myClosed, myOpen, closedIn(agentDeals), agentDeals.filter(isOpenDeal), undefined, (t) => settingsOf(t.ownerUid))
    : computeCommission(closedIn(mine, settingsOf(user?.uid)), myOpen, false, undefined, settingsOf(user?.uid));
  const overrideYtd = comm.overrideYtd?.net || 0;
  const overridePending = comm.overridePending?.net || 0;
  const unsetDeals = adminLayout ? deals.filter((t) => (t.ownerUid || user?.uid) !== user?.uid && !leadSourceOf(t) && String(t.status || '').toLowerCase() !== 'lost') : [];
  const pipelineTiles = (uidFilter, who) => (
    <div style={gridOf(160)}>
      <Tile label="Sellers in pipeline" value={sellerCount(uidFilter)} sub={`${who}: New, Contacted, Appointment set`} />
      <Tile label="Buyers in pipeline" value={0} sub="Buyer interest form coming soon" />
    </div>
  );
  const volumeTiles = (closed, open, scope) => (
    <div style={gridOf(160)}>
      <Tile label={`${scope}Closed volume YTD`} value={vol(closed)} sub={`${closed.length} closed this year`} />
      <Tile label={`${scope}Pending volume`} value={vol(open)} sub={`${open.length} open / under contract`} />
    </div>
  );
  const capLine = iAmLeader && (
    <div style={{ background: '#FBF5E3', border: `1px solid ${GOLD}`, borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 14 }}>
      <strong>Cap progress: {dollars(comm.capPaidYtd)} of {dollars(comm.cap)}</strong>
      <div style={{ color: '#666', fontSize: 12, marginTop: 4 }}>eXp {100 - COMMISSION.teamLeader.brokerPercent}/{COMMISSION.teamLeader.brokerPercent}, no team split. Resets {yearStart.toLocaleDateString([], { month: 'short', day: 'numeric' })} each year.{comm.capPaidWithPending > comm.capPaidYtd ? ` Pending deals would add ${dollars(comm.capPaidWithPending - comm.capPaidYtd)}.` : ''} Includes your own deals and team override income, in close-date order.</div>
    </div>
  );
  const td = { padding: '8px 6px', borderBottom: '1px solid #eee', textAlign: 'right', whiteSpace: 'nowrap' };

  let body;
  if (adminLayout) {
    const teamClosed = closedIn(deals);
    const teamOpen = deals.filter(isOpenDeal);
    body = (
      <>
        <div style={gridOf(340)}>
          <Section title="My Performance">
            {capLine}
            <div style={gridOf(240)}>
              <SplitTable title="YTD closed commission" s={comm.ytd} />
              <SplitTable title="Pending commission" s={comm.pending} />
            </div>
            <div style={gridOf(160)}>
              <Tile label="Team override income YTD" value={dollars(overrideYtd)} sub={`Share of agents' closed deals ${dollars(comm.overrideYtd?.gross)}, less eXp ${dollars(comm.overrideYtd?.broker)}`} />
              <Tile label="Team override pending" value={dollars(overridePending)} sub={`Share of agents' open deals ${dollars(comm.overridePending?.gross)}, less eXp ${dollars(comm.overridePending?.broker)}`} />
            </div>
            {pipelineTiles(user?.uid, 'Mine')}
          </Section>
          <Section title="Team Performance">
            {volumeTiles(teamClosed, teamOpen, 'Team ')}
            {pipelineTiles(null, 'Whole team')}
            <p style={{ fontSize: 12, color: '#888', margin: 0 }}>All agents, including you.</p>
          </Section>
        </div>
        <Section title="Commission by agent (YTD closed / pending)">
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, background: '#fff' }}>
              <thead><tr>{['Agent', 'Gross', 'Team leader share', 'eXp', 'Take-home', 'eXp cap paid', 'Pending take-home', 'Lead source not set'].map((h, n) => <th key={h} style={{ ...td, textAlign: n ? 'right' : 'left', fontWeight: 700 }}>{h}</th>)}</tr></thead>
              <tbody>{agentRows.map((r) => (
                <tr key={r.uid}>
                  <td style={{ ...td, textAlign: 'left' }}>{r.name}{r.leader ? ' (team leader)' : ''}</td>
                  <td style={td}>{dollars(r.ytd.gross)}</td><td style={td}>{dollars(r.ytd.leader)}</td><td style={td}>{dollars(r.ytd.broker)}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{dollars(r.ytd.net)}</td><td style={td}>{dollars(r.capPaidYtd)} / {dollars(r.cap)}</td>
                  <td style={td}>{dollars(r.pending.net)}</td><td style={td}>{r.ytd.unsetLeadSource + r.pending.unsetLeadSource || ''}</td>
                </tr>))}</tbody>
            </table>
          </div>
          {unsetDeals.length > 0 && (
            <div style={{ marginTop: 12, fontSize: 14 }}>
              <strong>Lead source not set ({unsetDeals.length})</strong> <span style={{ color: '#666' }}>— counted as team leads (50/50) until set. Open each deal, Edit, and pick the lead source:</span>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>{unsetDeals.map((t) => <li key={t.id}><Link to={`/transactions/${t.id}/edit`}>{t.propertyAddress || t.id}</Link> · {people[t.ownerUid]?.name || ''}</li>)}</ul>
            </div>
          )}
        </Section>
      </>
    );
  } else {
    body = (
      <>
        {pipelineTiles(user?.uid, 'Yours')}
        {volumeTiles(myClosed, myOpen, '')}
        <div style={gridOf(160)}>
          <Tile label="YTD Closed Commission" value={dollars(comm.ytd.net)} sub={`${myClosed.length} closed this year`} />
          <Tile label="Pending Commission" value={dollars(comm.pending.net)} sub={`${myOpen.length} open deals`} />
        </div>
      </>
    );
  }

  return (
    <div style={{ background: '#f5f5f5', color: '#1a1a1a', minHeight: '100vh', boxSizing: 'border-box', padding: 'clamp(12px, 3vw, 24px)', fontFamily: 'Roboto, -apple-system, sans-serif' }}>
      <h2 style={{ margin: '0 0 16px', fontSize: 24, fontWeight: 800 }}>{adminLayout ? 'Performance' : 'My Performance'}</h2>
      <OutreachCard uid={user?.uid} full />
      {adminLayout && <OutreachTeamTable />}
      {loading ? <p>Loading…</p> : body}
    </div>
  );
}
